import { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { formatDepartmentsDisplay } from '../../features/admin/utils/departmentUtils';
import { formatPhoneNumber, removeCountryCode } from '../../utils/phoneFormatter';
import { toBoolean } from '../../utils/booleanUtils';
import UserFormModal from '../../features/admin/components/UserFormModal';
import ActionDropdown from '../../features/admin/components/ActionDropdown';
import ConfirmationModal from '../../shared/components/modals/ConfirmationModal';
import Modal from '../../shared/components/modals/Modal';
import { EmployeeIDBadge } from '../../shared/components/common/Badges';
import RoleBadge from '../../shared/components/common/RoleBadge';
import Pagination from '../../shared/components/common/Pagination';
import MoreFiltersButton from '../../shared/components/common/MoreFiltersButton';
import StatsCard from '../../shared/components/common/StatsCard';
import { API_ENDPOINTS } from '../../config/apiConfig';
import LocationFilterMultiSelect from '../../shared/components/common/LocationFilterMultiSelect';
import LocationDisabledBadge from '../../shared/components/common/LocationDisabledBadge';
import DisabledLocationAccessBlockedBanner from '../../shared/components/common/DisabledLocationAccessBlockedBanner';
import {
  findLocationById,
  getDisabledLocationsFromSelection,
  isLocationActive,
} from '../../shared/utils/locationDisplay';
import ExportButton from '../../shared/components/modals/ExportButton';
import ExportColumnConfigurationModal from '../../shared/components/modals/ExportColumnConfigurationModal';
import UserDepartmentsModal from '../../shared/components/common/UserDepartmentsModal';
import { PDF_EXPORT_MAX_COLUMNS, validatePdfExportColumnCount } from '../../constants/exportColumnPolicy';
import {
  createUserExportColumnsState,
  getSelectedExportColumnHeaders,
} from './userExportColumns';
import { exportUsersToExcel, exportUsersToPdf } from './userExportUtils';
import './Users.css';

const STATUS_GROUP_LABELS = {
  active: 'Active',
  pending: 'Pending Verification',
  deactivated: 'Deactivated',
  password_setup: 'Password Setup Pending',
};

const userNeedsPasswordSetup = (u) => (
  u.is_active
  && u.email_verified
  && (u.password_reset_required === true || u.password_reset_required === 1)
);

const formatStatusGroupLabel = (statusGroup) => (
  STATUS_GROUP_LABELS[statusGroup] || statusGroup
);

function Users({ token, user, initialStatusGroup = null, initialLocations = null, onInitialStatusGroupApplied = null }) {
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUser, setCurrentUser] = useState(user);
  const [usernameError, setUsernameError] = useState('');
  const [modalError, setModalError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    userId: null,
    userName: '',
    isPendingVerification: false
  });
  const [deactivateConfirmation, setDeactivateConfirmation] = useState({
    isOpen: false,
    userId: null,
    userName: ''
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isFetchingDeletePreview, setIsFetchingDeletePreview] = useState(false);
  const [cannotDeleteInfo, setCannotDeleteInfo] = useState({
    isOpen: false,
    userName: ''
  });
  const [isSendingLink, setIsSendingLink] = useState(null);
  const [linkResendConfirmation, setLinkResendConfirmation] = useState({ isOpen: false, userId: null, userName: '', lastSentTime: null, emailVerified: true });
  const [isResendingLink, setIsResendingLink] = useState(false);
  const [isRevokingLink, setIsRevokingLink] = useState(null);
  const [revokeConfirmation, setRevokeConfirmation] = useState({ isOpen: false, userId: null, userName: '', emailVerified: true });
  const [sorting, setSorting] = useState({
    sort_by: 'full_name',
    sort_direction: 'asc'
  });

  const createInitialUserFilterState = (statusGroup = '') => ({
    statusGroup, // '' | 'active' | 'pending' | 'deactivated' | 'password_setup'
    roles: [],
    departments: [], // department IDs
    locations: [], // location IDs
    createdDateFrom: '', // YYYY-MM-DD
    createdDateTo: '', // YYYY-MM-DD
    lastLoginFrom: '', // YYYY-MM-DD
    lastLoginTo: '', // YYYY-MM-DD
    invitationState: '', // '' | 'active' | 'expired' | 'none'
    updatedRecently: '', // '' | '7d' | '30d' | '90d'
    multipleDepartments: false,
  });

  const [filters, setFilters] = useState(() => ({
    ...createInitialUserFilterState(initialStatusGroup || ''),
    locations: Array.isArray(initialLocations) ? initialLocations : [],
  }));
  const [showAdvancedPanel, setShowAdvancedPanel] = useState(false);
  const [drawerFilters, setDrawerFilters] = useState(() => ({
    ...createInitialUserFilterState(initialStatusGroup || ''),
    locations: Array.isArray(initialLocations) ? initialLocations : [],
  }));
  const [activeFilterOrder, setActiveFilterOrder] = useState([]); // append-only ordering of activated filter keys
  const [exportColumns, setExportColumns] = useState(() => createUserExportColumnsState());
  const [showExportColumnsConfig, setShowExportColumnsConfig] = useState(false);
  const [isExportingUsers, setIsExportingUsers] = useState(false);
  const [departmentsModalContext, setDepartmentsModalContext] = useState(null);

  const canExportUsers = (currentUser?.role || user?.role) === 'superadmin'
    || (currentUser?.role || user?.role) === 'admin';

  const blockedDisabledLocations = useMemo(
    () => (currentUser?.role === 'superadmin' ? getDisabledLocationsFromSelection(locations, filters.locations) : []),
    [currentUser?.role, locations, filters.locations]
  );
  const isDisabledLocationFilterBlocked = blockedDisabledLocations.length > 0;

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
    fetchLocations();
  }, []);

  useEffect(() => {
    const hasStatus = Boolean(initialStatusGroup);
    const normalizedInitialLocations = Array.isArray(initialLocations)
      ? initialLocations.map((id) => String(id))
      : [];
    const hasLocations = normalizedInitialLocations.length > 0;
    if (!hasStatus && !hasLocations) return;
    setFilters((prev) => ({
      ...prev,
      ...(hasStatus ? { statusGroup: initialStatusGroup } : {}),
      ...(hasLocations ? { locations: normalizedInitialLocations } : {}),
    }));
    setDrawerFilters((prev) => ({
      ...prev,
      ...(hasStatus ? { statusGroup: initialStatusGroup } : {}),
      ...(hasLocations ? { locations: normalizedInitialLocations } : {}),
    }));
    if (hasLocations) {
      setActiveFilterOrder((prev) => (prev.includes('locations') ? prev : [...prev, 'locations']));
    }
    setCurrentPage(1);
    if (typeof onInitialStatusGroupApplied === 'function') {
      onInitialStatusGroupApplied();
    }
  }, [initialStatusGroup, initialLocations, onInitialStatusGroupApplied]);

  // Prevent body scroll when advanced filter drawer is open
  useEffect(() => {
    if (showAdvancedPanel) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showAdvancedPanel]);

  const uniqueRoles = Array.from(new Set((users || []).map(u => u?.role).filter(Boolean))).sort((a, b) =>
    String(a).localeCompare(String(b))
  );

  const isFilterKeyActive = (filtersObj, key) => {
    switch (key) {
      case 'statusGroup':
        return !!filtersObj.statusGroup;
      case 'roles':
        return (filtersObj.roles || []).length > 0;
      case 'departments':
        return (filtersObj.departments || []).length > 0;
      case 'locations':
        return (filtersObj.locations || []).length > 0;
      case 'createdDateRange':
        return !!(filtersObj.createdDateFrom || filtersObj.createdDateTo);
      case 'lastLoginRange':
        return !!(filtersObj.lastLoginFrom || filtersObj.lastLoginTo);
      case 'invitationState':
        return !!filtersObj.invitationState;
      case 'updatedRecently':
        return !!filtersObj.updatedRecently;
      case 'multipleDepartments':
        return !!filtersObj.multipleDepartments;
      default:
        return false;
    }
  };

  // Maintain append-only order of filters as the user activates them
  useEffect(() => {
    const allKeys = [
      'statusGroup',
      'roles',
      'departments',
      'locations',
      'createdDateRange',
      'lastLoginRange',
      'invitationState',
      'updatedRecently',
      'multipleDepartments',
    ];
    setActiveFilterOrder(prevOrder => {
      let nextOrder = prevOrder.filter(key => allKeys.includes(key));

      // Remove keys that are no longer active
      nextOrder = nextOrder.filter(key => isFilterKeyActive(filters, key));

      // Append keys that became active but aren't yet in the order
      for (const key of allKeys) {
        if (isFilterKeyActive(filters, key) && !nextOrder.includes(key)) {
          nextOrder = [...nextOrder, key];
        }
      }

      return nextOrder;
    });
  }, [filters]);

  const canShowLocationFilter = () => {
    // Match Devices behavior: only show cross-location filtering when the user can see multiple locations.
    // In this app, that effectively means admin/superadmin, and the locations list has >1 entries.
    return (currentUser?.role === 'superadmin' || currentUser?.role === 'admin') && locations.length > 1;
  };

  const hasActiveFilters = () => {
    return !!(
      filters.statusGroup ||
      (filters.roles && filters.roles.length > 0) ||
      (filters.departments && filters.departments.length > 0) ||
      (filters.locations && filters.locations.length > 0) ||
      filters.createdDateFrom ||
      filters.createdDateTo ||
      filters.lastLoginFrom ||
      filters.lastLoginTo ||
      filters.invitationState ||
      filters.updatedRecently ||
      filters.multipleDepartments
    );
  };

  const openAdvancedFilters = () => {
    setDrawerFilters(filters);
    setShowAdvancedPanel(true);
  };

  const closeAdvancedFilters = () => {
    setShowAdvancedPanel(false);
  };

  const clearAllFilters = () => {
    setFilters(createInitialUserFilterState());
    setActiveFilterOrder([]);
  };

  const toggleArrayValue = (current, value) => {
    const strValue = String(value);
    const set = new Set((current || []).map(v => String(v)));
    if (set.has(strValue)) {
      return (current || []).filter(v => String(v) !== strValue);
    }
    return [...(current || []), value];
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.LOCATIONS.LIST, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.DEPARTMENTS.LIST, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const getUserDepartmentIds = (targetUser) => {
    if (!targetUser) return [];
    if (targetUser.department_ids?.length) return targetUser.department_ids;
    if (targetUser.departments?.length) return targetUser.departments.map((d) => d.id);
    return [];
  };

  const getUserDepartmentsForDisplay = (targetUser) => {
    if (targetUser?.departments?.length) return targetUser.departments;
    return getUserDepartmentIds(targetUser)
      .map((id) => departments.find((d) => d.id === id))
      .filter(Boolean);
  };

  const userMatchesDepartmentFilter = (targetUser, filterDeptIds) => {
    if (!filterDeptIds?.length) return true;
    const userDeptSet = new Set(getUserDepartmentIds(targetUser).map((id) => String(id)));
    return filterDeptIds.some((id) => userDeptSet.has(String(id)));
  };

  const userHasMultipleDepartments = (targetUser) => {
    if (targetUser?.departments?.length > 1) return true;
    return getUserDepartmentIds(targetUser).length > 1;
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(API_ENDPOINTS.USERS.LIST, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setUsers(response.data);

      // Fetch current user ID to prevent them from editing themselves
      try {
        const userResponse = await axios.get(API_ENDPOINTS.AUTH.ME, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setCurrentUserId(userResponse.data.id);
      } catch (err) {
        console.error('Error fetching current user info:', err);
        setCurrentUserId(null);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to fetch users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (userData) => {
    try {
      setModalError('');
      const dataToSend = { ...userData };

      // Keep contact_number as plain 10 digits (database stores plain format)
      // No formatting needed - just ensure it's 10 digits
      if (dataToSend.contact_number) {
        dataToSend.contact_number = dataToSend.contact_number.replace(/\D/g, '').slice(0, 10);
      }

     // Add location assignments if user role requires it
      if (userData.role !== 'superadmin') {
        dataToSend.location_ids = selectedLocations;
      }

      const deptScopedRoles = ['technician', 'custodian', 'coordinator'];
      if (deptScopedRoles.includes(userData.role)) {
        dataToSend.department_ids = selectedDepartments;
      }

      await axios.post(API_ENDPOINTS.USERS.CREATE, dataToSend, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      fetchUsers();
      resetForm();
      toast.success('User created successfully!');
    } catch (error) {
      console.error('Error creating user:', error);
      setModalError(error.response?.data?.error || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (userData) => {
    try {
      setModalError('');
      const updateData = { ...userData };
      if (!updateData.password) {
        delete updateData.password;
      }

      // Keep contact_number as plain 10 digits (database stores plain format)
      // No formatting needed - just ensure it's 10 digits
      if (updateData.contact_number) {
        updateData.contact_number = updateData.contact_number.replace(/\D/g, '').slice(0, 10);
      }

      // Phase 2: Check if trying to activate a deactivated user
      if (toBoolean(updateData.is_active) && selectedUser && !toBoolean(selectedUser.is_active)) {
        setModalError('Cannot activate user via edit form. Deactivated users must reactivate via email link. Please send them a reactivation link instead.');
        return;
      }

      // Add location assignments only when they actually changed (avoids Option A email+location guard on no-op echo)
      if (userData.role !== 'superadmin') {
        const toSortedNum = (ids) => [...(ids || [])].map((id) => Number(id)).sort((a, b) => a - b);
        const prevLoc = toSortedNum(selectedUser?.location_ids);
        const nextLoc = toSortedNum(selectedLocations);
        const locationsUnchanged =
          prevLoc.length === nextLoc.length && prevLoc.every((id, i) => id === nextLoc[i]);

        if (!locationsUnchanged) {
          updateData.location_ids = selectedLocations;
        }
      }

      const deptScopedRoles = ['technician', 'custodian', 'coordinator'];
      if (deptScopedRoles.includes(userData.role)) {
        const toSortedNum = (ids) => [...(ids || [])].map((id) => Number(id)).sort((a, b) => a - b);
        const prevDept = toSortedNum(getUserDepartmentIds(selectedUser));
        const nextDept = toSortedNum(selectedDepartments);
        const departmentsUnchanged =
          prevDept.length === nextDept.length && prevDept.every((id, i) => id === nextDept[i]);

        if (!departmentsUnchanged) {
          updateData.department_ids = selectedDepartments;
        }
      }

      await axios.put(API_ENDPOINTS.USERS.UPDATE(selectedUser.id), updateData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      fetchUsers();
      resetForm();
      toast.success('User updated successfully!');
    } catch (error) {
      console.error('Error updating user:', error);
      setModalError(error.response?.data?.error || 'Failed to update user');
    }
  };

  const handleDeleteClick = async (userId, fullName, username) => {
    // Only superadmin can delete users
    if (currentUser?.role !== 'superadmin') {
      toast.error('Only Superadmin can delete users');
      return;
    }

    const userName = `${fullName} (${username})`;

    if (isFetchingDeletePreview) {
      return;
    }

    setIsFetchingDeletePreview(true);
    try {
      const response = await axios.get(API_ENDPOINTS.USERS.DELETION_PREVIEW(userId), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const {
        recommended_action: recommendedAction,
        email_verified: emailVerified = true,
      } = response.data;

      if (recommendedAction === 'hard_delete') {
        setDeleteConfirmation({
          isOpen: true,
          userId,
          userName,
          isPendingVerification: !emailVerified,
        });
        return;
      }

      if (recommendedAction === 'cannot_delete') {
        setCannotDeleteInfo({
          isOpen: true,
          userName,
        });
        return;
      }

      // recommended_action === 'deactivate'
      setDeactivateConfirmation({
        isOpen: true,
        userId,
        userName,
      });
    } catch (error) {
      console.error('Error previewing user deletion:', error);
      toast.error(error.response?.data?.error || 'Failed to determine delete action');
    } finally {
      setIsFetchingDeletePreview(false);
    }
  };

  const handleDeleteConfirm = async () => {
    const { userId } = deleteConfirmation;
    setIsDeleting(true);

    try {
      const response = await axios.delete(API_ENDPOINTS.USERS.DELETE(userId), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      fetchUsers();

      const { deletion_type: deletionType } = response.data;
      if (deletionType === 'hard_delete') {
        toast.success('User permanently deleted', { autoClose: 3000 });
      } else {
        // Unexpected fallback (e.g. race with new FK records) — still report accurately
        toast.success('User deactivated', { autoClose: 3000 });
      }

      setDeleteConfirmation({
        isOpen: false,
        userId: null,
        userName: '',
        isPendingVerification: false,
      });
    } catch (error) {
      console.error('Error deleting user:', error);
      const code = error.response?.data?.code;
      if (code === 'deactivate_confirmation_required') {
        setDeleteConfirmation({
          isOpen: false,
          userId: null,
          userName: '',
          isPendingVerification: false,
        });
        setDeactivateConfirmation({
          isOpen: true,
          userId: deleteConfirmation.userId,
          userName: deleteConfirmation.userName,
        });
        return;
      }
      if (code === 'already_inactive') {
        setDeleteConfirmation({
          isOpen: false,
          userId: null,
          userName: '',
          isPendingVerification: false,
        });
        setCannotDeleteInfo({
          isOpen: true,
          userName: deleteConfirmation.userName,
        });
        return;
      }
      toast.error(error.response?.data?.error || 'Failed to delete user');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeactivateConfirm = async () => {
    const { userId } = deactivateConfirmation;
    setIsDeleting(true);

    try {
      await axios.delete(API_ENDPOINTS.USERS.DELETE_WITH_DEACTIVATE(userId), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      fetchUsers();
      toast.success('User deactivated', { autoClose: 3000 });

      setDeactivateConfirmation({
        isOpen: false,
        userId: null,
        userName: '',
      });
    } catch (error) {
      console.error('Error deactivating user:', error);
      const code = error.response?.data?.code;
      if (code === 'already_inactive') {
        setDeactivateConfirmation({
          isOpen: false,
          userId: null,
          userName: '',
        });
        setCannotDeleteInfo({
          isOpen: true,
          userName: deactivateConfirmation.userName,
        });
        return;
      }
      toast.error(error.response?.data?.error || 'Failed to deactivate user');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmation({
      isOpen: false,
      userId: null,
      userName: '',
      isPendingVerification: false,
    });
  };

  const handleDeactivateCancel = () => {
    setDeactivateConfirmation({
      isOpen: false,
      userId: null,
      userName: '',
    });
  };

  const handleCannotDeleteInfoClose = () => {
    setCannotDeleteInfo({
      isOpen: false,
      userName: '',
    });
  };

  const handleEditUser = async (user) => {
    setSelectedUser(user);

    // Fetch user's current locations
    try {
      const response = await axios.get(API_ENDPOINTS.USERS.GET(user.id), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const userLocations = response.data.locations || [];
      // API returns location objects with 'id' field, not 'location_id'
      setSelectedLocations(userLocations.map(loc => loc.id));
      setSelectedDepartments(
        response.data.department_ids
        || (response.data.departments || []).map((d) => d.id)
        || getUserDepartmentIds(user)
      );
    } catch (error) {
      console.error('Error fetching user locations:', error);
      setSelectedLocations([]);
    }

    setShowForm(true);
  };

  const resetForm = () => {
    setSelectedUser(null);
    setShowForm(false);
    setModalError('');
    setUsernameError('');
    setSelectedLocations([]);
    setSelectedDepartments([]);
  };

  // Helper function to format role name for display
  const formatRoleForSearch = (role) => {
    return role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, ' ');
  };

  const toDateInputValue = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const subtractDays = (baseDate, days) => {
    const d = new Date(baseDate);
    d.setDate(d.getDate() - days);
    return d;
  };

  const isCreatedLastNDaysActive = (days) => {
    const today = toDateInputValue(new Date());
    const from = toDateInputValue(subtractDays(new Date(), days));
    return filters.createdDateFrom === from && filters.createdDateTo === today;
  };

  const isLastLoginLastNDaysActive = (days) => {
    const today = toDateInputValue(new Date());
    const from = toDateInputValue(subtractDays(new Date(), days));
    return filters.lastLoginFrom === from && filters.lastLoginTo === today;
  };

  const isUpdatedRecentlyLastNDaysActive = (days) => {
    return (
      (days === 7 && filters.updatedRecently === '7d') ||
      (days === 30 && filters.updatedRecently === '30d') ||
      (days === 90 && filters.updatedRecently === '90d')
    );
  };

  const hasAdditionalFilters = Boolean(
    (filters.roles && filters.roles.length > 0)
    || (filters.departments && filters.departments.length > 0)
    || (filters.locations && filters.locations.length > 0)
    || ((filters.createdDateFrom || filters.createdDateTo) && !isCreatedLastNDaysActive(7) && !isCreatedLastNDaysActive(30))
    || ((filters.lastLoginFrom || filters.lastLoginTo) && !isLastLoginLastNDaysActive(7) && !isLastLoginLastNDaysActive(30))
    || (filters.invitationState && filters.invitationState !== 'expired')
    || (filters.updatedRecently && filters.updatedRecently !== '7d' && filters.updatedRecently !== '30d')
  );

  const applyUserPreset = (presetName) => {
    if (presetName === 'created-7d') {
      if (isCreatedLastNDaysActive(7)) {
        setFilters(prev => ({ ...prev, createdDateFrom: '', createdDateTo: '' }));
        return;
      }
      const today = toDateInputValue(new Date());
      const from = toDateInputValue(subtractDays(new Date(), 7));
      setFilters(prev => ({ ...prev, createdDateFrom: from, createdDateTo: today }));
      return;
    }

    if (presetName === 'created-30d') {
      if (isCreatedLastNDaysActive(30)) {
        setFilters(prev => ({ ...prev, createdDateFrom: '', createdDateTo: '' }));
        return;
      }
      const today = toDateInputValue(new Date());
      const from = toDateInputValue(subtractDays(new Date(), 30));
      setFilters(prev => ({ ...prev, createdDateFrom: from, createdDateTo: today }));
      return;
    }

    if (presetName === 'login-7d') {
      if (isLastLoginLastNDaysActive(7)) {
        setFilters(prev => ({ ...prev, lastLoginFrom: '', lastLoginTo: '' }));
        return;
      }
      const today = toDateInputValue(new Date());
      const from = toDateInputValue(subtractDays(new Date(), 7));
      setFilters(prev => ({ ...prev, lastLoginFrom: from, lastLoginTo: today }));
      return;
    }

    if (presetName === 'login-30d') {
      if (isLastLoginLastNDaysActive(30)) {
        setFilters(prev => ({ ...prev, lastLoginFrom: '', lastLoginTo: '' }));
        return;
      }
      const today = toDateInputValue(new Date());
      const from = toDateInputValue(subtractDays(new Date(), 30));
      setFilters(prev => ({ ...prev, lastLoginFrom: from, lastLoginTo: today }));
      return;
    }

    if (presetName === 'invite-expired') {
      const isActive = filters.invitationState === 'expired';
      setFilters(prev => ({ ...prev, invitationState: isActive ? '' : 'expired' }));
      return;
    }

    if (presetName === 'updated-7d') {
      const isActive = isUpdatedRecentlyLastNDaysActive(7);
      setFilters(prev => ({ ...prev, updatedRecently: isActive ? '' : '7d' }));
      return;
    }

    if (presetName === 'updated-30d') {
      const isActive = isUpdatedRecentlyLastNDaysActive(30);
      setFilters(prev => ({ ...prev, updatedRecently: isActive ? '' : '30d' }));
      return;
    }

    if (presetName === 'password-setup') {
      const isActive = filters.statusGroup === 'password_setup';
      setFilters(prev => ({ ...prev, statusGroup: isActive ? '' : 'password_setup' }));
      setCurrentPage(1);
      return;
    }

    if (presetName === 'multiple-departments') {
      setFilters(prev => ({ ...prev, multipleDepartments: !prev.multipleDepartments }));
      setCurrentPage(1);
    }
  };

  /**
   * Handle column sort change - toggle direction if same column, or set new column with asc
   */
  const handleSortChange = (column) => {
    setSorting(prev => {
      // If same column clicked, toggle direction; otherwise set new column with asc
      if (prev.sort_by === column) {
        return {
          ...prev,
          sort_direction: prev.sort_direction === 'asc' ? 'desc' : 'asc'
        };
      } else {
        return {
          sort_by: column,
          sort_direction: 'asc'
        };
      }
    });
  };

  /**
   * Get sort indicator for column header (▲ for asc, ▼ for desc)
   */
  const getSortIndicator = (column) => {
    if (sorting.sort_by !== column) return '';
    return sorting.sort_direction === 'asc' ? ' ▲' : ' ▼';
  };

  // Helper function to apply search query to users
  const applySearchFilter = (usersToFilter) => {
    if (!searchQuery.trim()) {
      return usersToFilter;
    }

    const query = searchQuery.toLowerCase();
    return usersToFilter.filter(user => {
      // Get department name for this user
      const userDeptNames = getUserDepartmentsForDisplay(user)
        .map((dept) => dept.name.toLowerCase());
      const departmentName = userDeptNames.length > 1
        ? `multiple departments ${userDeptNames.join(' ')}`
        : userDeptNames.join(' ');

      // Get location names for this user from location_ids
      const userLocationNames = (user.location_ids || [])
        .map(locId => locations.find(loc => loc.id === locId)?.name)
        .filter(Boolean)
        .map(name => name.toLowerCase());

      return (
        (user.username || '').toLowerCase().includes(query) ||
        (user.employee_id || '').toLowerCase().includes(query) ||
        (user.full_name || '').toLowerCase().includes(query) ||
        (user.email || '').toLowerCase().includes(query) ||
        (user.role || '').toLowerCase().includes(query) ||
        formatRoleForSearch(user.role || '').toLowerCase().includes(query) ||
        departmentName.includes(query) ||
        userLocationNames.some(locName => locName.includes(query))
      );
    });
  };

  // Helper function to calculate stats based on search query
  const getStats = () => {
    // Start with all users excluding current user
    let baseUsers = users.filter(user => user.id !== currentUserId);

    // Apply all active filters EXCEPT statusGroup, so stats cards show distribution
    // within the currently narrowed result set (role/department/location/date/search).
    if (filters.roles && filters.roles.length > 0) {
      const roleSet = new Set(filters.roles);
      baseUsers = baseUsers.filter(user => roleSet.has(user.role));
    }

    if (filters.departments && filters.departments.length > 0) {
      baseUsers = baseUsers.filter((u) => userMatchesDepartmentFilter(u, filters.departments));
    }

    if (filters.multipleDepartments) {
      baseUsers = baseUsers.filter(userHasMultipleDepartments);
    }

    if (filters.locations && filters.locations.length > 0) {
      const locSet = new Set(filters.locations.map(l => String(l)));
      baseUsers = baseUsers.filter(user =>
        (user.location_ids || []).some(locId => locSet.has(String(locId)))
      );
    }

    if (filters.createdDateFrom || filters.createdDateTo) {
      baseUsers = baseUsers.filter(user => {
        if (!user.created_at) return false;
        const d = new Date(user.created_at);
        if (Number.isNaN(d.getTime())) return false;
        const created = toDateInputValue(d);
        if (filters.createdDateFrom && created < filters.createdDateFrom) return false;
        if (filters.createdDateTo && created > filters.createdDateTo) return false;
        return true;
      });
    }

    if (filters.lastLoginFrom || filters.lastLoginTo) {
      baseUsers = baseUsers.filter(user => {
        if (!user.last_login_at) return false;
        const d = new Date(user.last_login_at);
        if (Number.isNaN(d.getTime())) return false;
        const lastLogin = toDateInputValue(d);
        if (filters.lastLoginFrom && lastLogin < filters.lastLoginFrom) return false;
        if (filters.lastLoginTo && lastLogin > filters.lastLoginTo) return false;
        return true;
      });
    }

    if (filters.invitationState) {
      baseUsers = baseUsers.filter(user => {
        const link = user.pending_link;
        const hasLink = !!link;
        const isExpired = !!link?.is_expired;
        if (filters.invitationState === 'none') return !hasLink;
        if (filters.invitationState === 'active') return hasLink && !isExpired;
        if (filters.invitationState === 'expired') return hasLink && isExpired;
        return true;
      });
    }

    if (filters.updatedRecently) {
      const days = filters.updatedRecently === '7d' ? 7 : filters.updatedRecently === '30d' ? 30 : filters.updatedRecently === '90d' ? 90 : null;
      if (days) {
        const now = new Date();
        const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
        const fromStr = toDateInputValue(from);
        const toStr = toDateInputValue(now);
        baseUsers = baseUsers.filter(user => {
          if (!user.updated_at) return false;
          const d = new Date(user.updated_at);
          if (Number.isNaN(d.getTime())) return false;
          const updated = toDateInputValue(d);
          return updated >= fromStr && updated <= toStr;
        });
      }
    }

    const searchedUsers = applySearchFilter(baseUsers);

    // Calculate counts for each category
    return {
      total: searchedUsers.length,
      active: searchedUsers.filter(u => u.is_active).length,
      pending: searchedUsers.filter(u => !u.is_active && !u.email_verified).length,
      deactivated: searchedUsers.filter(u => !u.is_active && u.email_verified).length,
      passwordSetupPending: searchedUsers.filter(userNeedsPasswordSetup).length,
    };
  };

  const getFilteredUsers = () => {
    // First, exclude the current user from the list
    let usersExcludingSelf = users.filter(user => user.id !== currentUserId);

    if (isDisabledLocationFilterBlocked) {
      return [];
    }

    // Apply status group filter (driven by clickable stats cards + advanced filters)
    if (filters.statusGroup) {
      if (filters.statusGroup === 'active') {
        usersExcludingSelf = usersExcludingSelf.filter(user => user.is_active);
      } else if (filters.statusGroup === 'pending') {
        usersExcludingSelf = usersExcludingSelf.filter(user => !user.is_active && !user.email_verified);
      } else if (filters.statusGroup === 'deactivated') {
        usersExcludingSelf = usersExcludingSelf.filter(user => !user.is_active && user.email_verified);
      } else if (filters.statusGroup === 'password_setup') {
        usersExcludingSelf = usersExcludingSelf.filter(userNeedsPasswordSetup);
      }
    }

    // Role filter
    if (filters.roles && filters.roles.length > 0) {
      const roleSet = new Set(filters.roles);
      usersExcludingSelf = usersExcludingSelf.filter(user => roleSet.has(user.role));
    }

    // Department filter (IDs)
    if (filters.departments && filters.departments.length > 0) {
      usersExcludingSelf = usersExcludingSelf.filter((u) => userMatchesDepartmentFilter(u, filters.departments));
    }

    if (filters.multipleDepartments) {
      usersExcludingSelf = usersExcludingSelf.filter(userHasMultipleDepartments);
    }

    // Location filter (IDs) - user matches if any user.location_ids intersects
    if (filters.locations && filters.locations.length > 0) {
      const locSet = new Set(filters.locations.map(l => String(l)));
      usersExcludingSelf = usersExcludingSelf.filter(user =>
        (user.location_ids || []).some(locId => locSet.has(String(locId)))
      );
    }

    // Created date range filter (inclusive)
    if (filters.createdDateFrom || filters.createdDateTo) {
      usersExcludingSelf = usersExcludingSelf.filter(user => {
        if (!user.created_at) return false;
        const d = new Date(user.created_at);
        if (Number.isNaN(d.getTime())) return false;
        const created = toDateInputValue(d);
        if (filters.createdDateFrom && created < filters.createdDateFrom) return false;
        if (filters.createdDateTo && created > filters.createdDateTo) return false;
        return true;
      });
    }

    // Last login range filter (inclusive)
    if (filters.lastLoginFrom || filters.lastLoginTo) {
      usersExcludingSelf = usersExcludingSelf.filter(user => {
        // last_login_at is derived on backend from login_attempts
        if (!user.last_login_at) return false;
        const d = new Date(user.last_login_at);
        if (Number.isNaN(d.getTime())) return false;
        const lastLogin = toDateInputValue(d);
        if (filters.lastLoginFrom && lastLogin < filters.lastLoginFrom) return false;
        if (filters.lastLoginTo && lastLogin > filters.lastLoginTo) return false;
        return true;
      });
    }

    // Invitation state filter (verification/reactivation link)
    if (filters.invitationState) {
      usersExcludingSelf = usersExcludingSelf.filter(user => {
        const link = user.pending_link; // may be null, or { created_at, expires_at, is_expired }
        const hasLink = !!link;
        const isExpired = !!link?.is_expired;
        if (filters.invitationState === 'none') return !hasLink;
        if (filters.invitationState === 'active') return hasLink && !isExpired;
        if (filters.invitationState === 'expired') return hasLink && isExpired;
        return true;
      });
    }

    // Updated recently filter (based on updated_at, inclusive)
    if (filters.updatedRecently) {
      const days = filters.updatedRecently === '7d' ? 7 : filters.updatedRecently === '30d' ? 30 : filters.updatedRecently === '90d' ? 90 : null;
      if (days) {
        const now = new Date();
        const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
        const fromStr = toDateInputValue(from);
        const toStr = toDateInputValue(now);
        usersExcludingSelf = usersExcludingSelf.filter(user => {
          if (!user.updated_at) return false;
          const d = new Date(user.updated_at);
          if (Number.isNaN(d.getTime())) return false;
          const updated = toDateInputValue(d);
          return updated >= fromStr && updated <= toStr;
        });
      }
    }

    // Apply search query filter
    let filtered = applySearchFilter(usersExcludingSelf);

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;

      // Get sort values based on sort_by column
      switch (sorting.sort_by) {
        case 'username':
          aValue = (a.username || '').toLowerCase();
          bValue = (b.username || '').toLowerCase();
          break;
        case 'full_name':
          aValue = (a.full_name || '').toLowerCase();
          bValue = (b.full_name || '').toLowerCase();
          break;
        case 'email':
          aValue = (a.email || '').toLowerCase();
          bValue = (b.email || '').toLowerCase();
          break;
        case 'role':
          aValue = (a.role || '').toLowerCase();
          bValue = (b.role || '').toLowerCase();
          break;
        case 'department': {
          const deptA = formatDepartmentsDisplay(getUserDepartmentsForDisplay(a)).sortKey;
          const deptB = formatDepartmentsDisplay(getUserDepartmentsForDisplay(b)).sortKey;
          aValue = deptA;
          bValue = deptB;
          break;
        }
        case 'location':
          // Sort by first (primary) location name
          const aLocations = (a.location_ids || [])
            .map(locId => locations.find(loc => loc.id === locId)?.name)
            .filter(Boolean);
          const bLocations = (b.location_ids || [])
            .map(locId => locations.find(loc => loc.id === locId)?.name)
            .filter(Boolean);
          aValue = aLocations.length > 0 ? aLocations[0].toLowerCase() : '';
          bValue = bLocations.length > 0 ? bLocations[0].toLowerCase() : '';
          break;
        case 'status':
          // Sort by active status: active first, then by email_verified status
          aValue = a.is_active ? 0 : (a.email_verified ? 2 : 1);
          bValue = b.is_active ? 0 : (b.email_verified ? 2 : 1);
          break;
        case 'created_at':
          aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
          bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        case 'updated_at':
          aValue = a.updated_at ? new Date(a.updated_at).getTime() : 0;
          bValue = b.updated_at ? new Date(b.updated_at).getTime() : 0;
          break;
        default:
          aValue = (a.full_name || '').toLowerCase();
          bValue = (b.full_name || '').toLowerCase();
      }

      // Compare values
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        // String comparison
        const result = aValue.localeCompare(bValue);
        return sorting.sort_direction === 'asc' ? result : -result;
      } else {
        // Numeric comparison
        if (aValue !== bValue) {
          return sorting.sort_direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
      }

      return 0;
    });

    return filtered;
  };

  const filteredUsers = getFilteredUsers();

  // Pagination logic
  const totalPages = Math.ceil(filteredUsers.length / perPage);
  const startIndex = (currentPage - 1) * perPage;
  const endIndex = startIndex + perPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  const exportContext = useMemo(() => {
    const role = currentUser?.role || user?.role || '';
    let locationLabel = '';
    if (role === 'superadmin') {
      const selected = filters.locations || [];
      if (selected.length === 1) {
        locationLabel = locations.find((loc) => String(loc.id) === String(selected[0]))?.name || '1 location';
      } else if (selected.length > 1) {
        locationLabel = `${selected.length} locations`;
      } else {
        locationLabel = 'All locations';
      }
    } else {
      const locId = currentUser?.location_id || user?.location_id;
      locationLabel = locations.find((loc) => String(loc.id) === String(locId))?.name
        || currentUser?.location_name
        || user?.location_name
        || '';
    }

    return {
      locations,
      getUserDepartmentsForDisplay,
      organizationName: currentUser?.organization_name || user?.organization_name || '',
      locationLabel,
      locationName: locationLabel,
      role,
      selectedLocationIds: filters.locations || [],
    };
  }, [locations, departments, filters.locations, currentUser, user]);

  const handleExportUsersExcel = async () => {
    const selected = getSelectedExportColumnHeaders(exportColumns);
    if (selected.length === 0) {
      toast.error('Select at least one column to export');
      return;
    }
    setIsExportingUsers(true);
    try {
      const count = await exportUsersToExcel(filteredUsers, selected, exportContext);
      toast.success(`Exported ${count} user${count !== 1 ? 's' : ''} to Excel`);
    } catch (error) {
      console.error('Error exporting users to Excel:', error);
      toast.error(error.message || 'Failed to export Excel');
    } finally {
      setIsExportingUsers(false);
    }
  };

  const handleExportUsersPdf = async () => {
    const selected = getSelectedExportColumnHeaders(exportColumns);
    if (selected.length === 0) {
      toast.error('Select at least one column to export');
      return;
    }
    const pdfCheck = validatePdfExportColumnCount(selected.length);
    if (!pdfCheck.ok) {
      toast.error(pdfCheck.message);
      return;
    }
    setIsExportingUsers(true);
    try {
      const count = await exportUsersToPdf(filteredUsers, selected, exportContext);
      toast.success(`Exported ${count} user${count !== 1 ? 's' : ''} to PDF`);
    } catch (error) {
      console.error('Error exporting users to PDF:', error);
      toast.error(error.message || 'Failed to export PDF');
    } finally {
      setIsExportingUsers(false);
    }
  };

  // Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filters]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  // Helper function to format time since link was sent (verification email or reactivation link)
  const getTimeSinceLinkSent = (createdAt) => {
    if (!createdAt) return null;

    const now = new Date();
    const sentTime = new Date(createdAt);
    const diffMs = now - sentTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return { date: '-', time: '' };
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return { date: '-', time: '' };
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const handleSendPendingLink = (userId, userFullName, user) => {
    // Phase 1 & 2: Send verification/reactivation link
    // If a link has already been sent, show confirmation modal for resend
    if (user.pending_link && !user.pending_link.is_expired) {
      setLinkResendConfirmation({
        isOpen: true,
        userId,
        userName: userFullName,
        lastSentTime: user.pending_link.created_at,
        emailVerified: user.email_verified
      });
      return;
    }

    // Send directly if no previous link exists (or it is expired)
    sendLinkAsync(userId, userFullName);
  };

  const sendLinkAsync = async (userId, userFullName) => {
    setIsSendingLink(userId);
    try {
      // Get user to check if they're new (Phase 1) or deactivated (Phase 2)
      const targetUser = users.find(u => u.id === userId);

      // Determine which endpoint to call based on email_verified status
      // Phase 1: Unverified user -> send verification email
      // Phase 2: Verified but deactivated user -> send reactivation email
      const isUnverified = !targetUser?.email_verified;
      const endpoint = isUnverified
        ? API_ENDPOINTS.EMAIL.SEND_VERIFICATION_EMAIL(userId)
        : API_ENDPOINTS.EMAIL.SEND_REACTIVATION_EMAIL(userId);

      const response = await axios.post(
        endpoint,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      fetchUsers();

      // Different message for Phase 1 (new users) vs Phase 2 (deactivated users)
      if (isUnverified) {
        toast.success(`Verification email sent to ${userFullName}`);
      } else {
        toast.success(`Reactivation link sent to ${userFullName}`);
      }
    } catch (error) {
      console.error('[Users Page] Error sending link:', error);
      const errorMsg = error.response?.data?.error || 'Failed to send link';
      toast.error(errorMsg);
    } finally {
      setIsSendingLink(null);
    }
  };

  const handleLinkResendConfirm = async () => {
    setIsResendingLink(true);
    await sendLinkAsync(
      linkResendConfirmation.userId,
      linkResendConfirmation.userName
    );
    setLinkResendConfirmation({ isOpen: false, userId: null, userName: '', lastSentTime: null, emailVerified: true });
    setIsResendingLink(false);
  };

  const handleLinkResendCancel = () => {
    setLinkResendConfirmation({ isOpen: false, userId: null, userName: '', lastSentTime: null, emailVerified: true });
  };

  const handleStatsCardClick = (filterType) => {
    setFilters(prev => {
      const next = filterType || '';
      return {
        ...prev,
        statusGroup: prev.statusGroup === next ? '' : next
      };
    });
    setCurrentPage(1);
  };

  const handleRevokeLinkClick = (userId, userFullName, emailVerified) => {
    setRevokeConfirmation({
      isOpen: true,
      userId,
      userName: userFullName,
      emailVerified
    });
  };

  const handleRevokeConfirm = async () => {
    const { userId, userName, emailVerified } = revokeConfirmation;
    setIsRevokingLink(userId);

    try {
      await axios.post(
        API_ENDPOINTS.USERS.REVOKE_PENDING_LINK(userId),
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      fetchUsers();

      // Show different message based on phase
      if (!emailVerified) {
        toast.success(`Verification email link revoked for ${userName}`);
      } else {
        toast.success(`Reactivation link revoked for ${userName}`);
      }
    } catch (error) {
      console.error('[Users Page] Error revoking link:', error);
      const errorMsg = error.response?.data?.error || 'Failed to revoke link';
      toast.error(errorMsg);
    } finally {
      setIsRevokingLink(null);
      setRevokeConfirmation({ isOpen: false, userId: null, userName: '', emailVerified: true });
    }
  };

  const handleRevokeCancel = () => {
    setRevokeConfirmation({ isOpen: false, userId: null, userName: '', emailVerified: true });
  };

  return (
    <div className="admin-portal users-page">
      <div className="users-description-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h1>👥 User Management</h1>
          <button onClick={fetchUsers} className="btn-refresh" title="Refresh users" style={{ padding: '0.5rem', fontSize: '1.2rem', border: 'none', background: 'none', cursor: 'pointer', lineHeight: 1 }}>
            🔄
          </button>
        </div>
        <p>Create, update, and manage users across different roles and departments. Assign locations and control access permissions.</p>
      </div>

      <div className="users-self-account-notice" role="status">
        <span className="users-self-account-notice-icon" aria-hidden="true">ℹ️</span>
        {currentUser?.role === 'superadmin' ? (
          <>
            Your own user account is excluded from the list and statistics below. You cannot edit or delete your own account from here. You can update your contact number in Profile Settings; for other account changes, contact your IT team.
          </>
        ) : (
          <>
            Your own user account is excluded from the list and statistics below. You cannot edit or delete your own account from here. Update your contact number in Profile Settings; for any other profile changes, contact your Super Admin.
          </>
        )}
      </div>

      <div className="users-header">
        <div className="header-left">
          {(() => {
            const stats = getStats();
            return (
              <div className="stats-summary stats-summary--with-readiness">
                <div className={`stats-card-wrapper ${filters.statusGroup === '' ? 'active' : ''}`.trim()} onClick={() => handleStatsCardClick(null)}>
                  <StatsCard
                    icon="👥"
                    number={stats.total}
                    label="Total Users"
                    className="total"
                  />
                </div>
                <div className={`stats-card-wrapper ${filters.statusGroup === 'active' ? 'active' : ''}`.trim()} onClick={() => handleStatsCardClick('active')}>
                  <StatsCard
                    icon="✅"
                    number={stats.active}
                    label="Active"
                    className="active"
                  />
                </div>
                <div className={`stats-card-wrapper ${filters.statusGroup === 'pending' ? 'active' : ''}`.trim()} onClick={() => handleStatsCardClick('pending')}>
                  <StatsCard
                    icon="📧"
                    number={stats.pending}
                    label="Pending Verification"
                    className="pending"
                  />
                </div>
                <div className={`stats-card-wrapper ${filters.statusGroup === 'deactivated' ? 'active' : ''}`.trim()} onClick={() => handleStatsCardClick('deactivated')}>
                  <StatsCard
                    icon="🔗"
                    number={stats.deactivated}
                    label="Deactivated"
                    className="deactivated"
                  />
                </div>
                <div className="stats-summary-separator" aria-hidden="true" />
                <div
                  className={`stats-card-wrapper stats-card-wrapper--readiness ${filters.statusGroup === 'password_setup' ? 'active' : ''}`.trim()}
                  onClick={() => handleStatsCardClick('password_setup')}
                  title="Active users who must set their password before full app access"
                >
                  <StatsCard
                    icon="🔑"
                    number={stats.passwordSetupPending}
                    label="Password Setup Pending"
                    className="password-setup"
                  />
                </div>
              </div>
            );
          })()}
        </div>
        {!showForm && (
          <div className="header-right">
            <div className="action-palette">
              <button className="action-btn action-primary" onClick={() => { setSelectedDepartments([]); setShowForm(true); }}>
                <span className="action-icon">➕</span>
                <span className="action-text">Add New User</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="error-container">
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        </div>
      )}

      {loading && (
        <div className="loading-container">
          <p>Loading users...</p>
        </div>
      )}

      {!loading && (
        <>
          <div className="search-box-wrapper">
            <span className="search-icon-lens">🔍</span>
            <input
              type="text"
              placeholder="Search by username, employee ID, full name, email, role, department, or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input-clean"
            />
            {searchQuery && (
              <button
                className="clear-search-btn-inline"
                onClick={() => setSearchQuery('')}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          {/* QUICK FILTERS (PRESETS) */}
          <div className="quick-filters">
            <div className="quick-filters-title">
              <svg className="filters-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              Quick Filters
            </div>
            <div className="preset-buttons">
              <button
                className={`preset-btn ${!hasActiveFilters() ? 'active' : ''}`}
                onClick={() => {
                  clearAllFilters();
                  setCurrentPage(1);
                }}
                title="Show all users (clear filters)"
              >
                All
              </button>
              <button
                className={`preset-btn ${isCreatedLastNDaysActive(7) ? 'active' : ''}`}
                onClick={() => applyUserPreset('created-7d')}
                title="Users created in the last 7 days"
              >
                Created (Last 7d)
              </button>
              <button
                className={`preset-btn ${isCreatedLastNDaysActive(30) ? 'active' : ''}`}
                onClick={() => applyUserPreset('created-30d')}
                title="Users created in the last 30 days"
              >
                Created (Last 30d)
              </button>
              <button
                className={`preset-btn ${isLastLoginLastNDaysActive(7) ? 'active' : ''}`}
                onClick={() => applyUserPreset('login-7d')}
                title="Users who logged in at least once in the last 7 days"
              >
                Login (Last 7d)
              </button>
              <button
                className={`preset-btn ${isLastLoginLastNDaysActive(30) ? 'active' : ''}`}
                onClick={() => applyUserPreset('login-30d')}
                title="Users who logged in at least once in the last 30 days"
              >
                Login (Last 30d)
              </button>
              <button
                className={`preset-btn ${filters.invitationState === 'expired' ? 'active' : ''}`}
                onClick={() => applyUserPreset('invite-expired')}
                title="Users with an expired verification/reactivation link"
              >
                Invite expired
              </button>
              <button
                className={`preset-btn ${filters.statusGroup === 'password_setup' ? 'active' : ''}`}
                onClick={() => applyUserPreset('password-setup')}
                title="Active users who must set their password before full app access"
              >
                Password setup pending
              </button>
              <button
                className={`preset-btn ${filters.multipleDepartments ? 'active' : ''}`}
                onClick={() => applyUserPreset('multiple-departments')}
                title="Users assigned to more than one department"
              >
                Multiple departments
              </button>
              <button
                className={`preset-btn ${isUpdatedRecentlyLastNDaysActive(7) ? 'active' : ''}`}
                onClick={() => applyUserPreset('updated-7d')}
                title="Users updated in the last 7 days"
              >
                Updated (Last 7d)
              </button>
              <button
                className={`preset-btn ${isUpdatedRecentlyLastNDaysActive(30) ? 'active' : ''}`}
                onClick={() => applyUserPreset('updated-30d')}
                title="Users updated in the last 30 days"
              >
                Updated (Last 30d)
              </button>
              <MoreFiltersButton
                isOpen={showAdvancedPanel}
                hasAdditional={hasAdditionalFilters}
                onClick={() => {
                  if (!showAdvancedPanel) {
                    openAdvancedFilters();
                  } else {
                    closeAdvancedFilters();
                  }
                }}
              />
            </div>
          </div>

          {/* ACTIVE FILTERS DISPLAY */}
          {hasActiveFilters() && (
            <div className="active-filters">
              <div className="active-filters-header">
                <span className="active-filters-label">Filters Active:</span>
                <button className="clear-all-btn" onClick={clearAllFilters}>
                  Clear All
                </button>
              </div>

              <div className="active-filters-pills">
                {(() => {
                  const pills = [];

                  const pushWithAnd = (node) => {
                    if (pills.length > 0) {
                      pills.push(
                        <span key={`and-${pills.length}`} className="filter-and-connector"> AND </span>
                      );
                    }
                    pills.push(node);
                  };

                  for (const key of activeFilterOrder) {
                    if (key === 'statusGroup' && filters.statusGroup) {
                      pushWithAnd(
                        <div className="filter-pill" key="statusGroup-pill">
                          Status: {formatStatusGroupLabel(filters.statusGroup)}
                          <span
                            className="filter-pill-remove"
                            onClick={() => setFilters(prev => ({ ...prev, statusGroup: '' }))}
                          >
                            ✕
                          </span>
                        </div>
                      );
                    }

                    if (key === 'roles' && filters.roles && filters.roles.length > 0) {
                      pushWithAnd(
                        <div className="filter-pill" key="roles-pill">
                          Roles: {filters.roles.map(r => formatRoleForSearch(r)).join(', ')}
                          <span
                            className="filter-pill-remove"
                            onClick={() => setFilters(prev => ({ ...prev, roles: [] }))}
                          >
                            ✕
                          </span>
                        </div>
                      );
                    }

                    if (key === 'departments' && filters.departments && filters.departments.length > 0) {
                      pushWithAnd(
                        <div className="filter-pill" key="departments-pill">
                          Departments: {filters.departments.map(deptId => {
                            const dept = departments.find(d => String(d.id) === String(deptId));
                            return dept ? dept.name : deptId;
                          }).join(', ')}
                          <span
                            className="filter-pill-remove"
                            onClick={() => setFilters(prev => ({ ...prev, departments: [] }))}
                          >
                            ✕
                          </span>
                        </div>
                      );
                    }

                    if (key === 'locations' && filters.locations && filters.locations.length > 0) {
                      pushWithAnd(
                        <div className="filter-pill" key="locations-pill">
                          <span className="filter-pill-location-list">
                            Locations:{' '}
                            {filters.locations.map((locId, index) => {
                              const loc = findLocationById(locations, locId);
                              return (
                                <span key={locId} className="filter-pill-location-item">
                                  {index > 0 ? ', ' : ''}
                                  {loc ? loc.name : locId}
                                  {loc && !isLocationActive(loc) && <LocationDisabledBadge />}
                                </span>
                              );
                            })}
                          </span>
                          <span
                            className="filter-pill-remove"
                            onClick={() => setFilters(prev => ({ ...prev, locations: [] }))}
                          >
                            ✕
                          </span>
                        </div>
                      );
                    }

                    if (key === 'createdDateRange' && (filters.createdDateFrom || filters.createdDateTo)) {
                      pushWithAnd(
                        <div className="filter-pill" key="createdDate-pill">
                          Created: {filters.createdDateFrom || '…'} to {filters.createdDateTo || '…'}
                          <span
                            className="filter-pill-remove"
                            onClick={() => setFilters(prev => ({ ...prev, createdDateFrom: '', createdDateTo: '' }))}
                          >
                            ✕
                          </span>
                        </div>
                      );
                    }

                    if (key === 'lastLoginRange' && (filters.lastLoginFrom || filters.lastLoginTo)) {
                      pushWithAnd(
                        <div className="filter-pill" key="lastLogin-pill">
                          Last login: {filters.lastLoginFrom || '…'} to {filters.lastLoginTo || '…'}
                          <span
                            className="filter-pill-remove"
                            onClick={() => setFilters(prev => ({ ...prev, lastLoginFrom: '', lastLoginTo: '' }))}
                          >
                            ✕
                          </span>
                        </div>
                      );
                    }

                    if (key === 'invitationState' && filters.invitationState) {
                      const label =
                        filters.invitationState === 'active'
                          ? 'Active link'
                          : filters.invitationState === 'expired'
                            ? 'Expired link'
                            : 'No link sent';
                      pushWithAnd(
                        <div className="filter-pill" key="invitationState-pill">
                          Invitation: {label}
                          <span
                            className="filter-pill-remove"
                            onClick={() => setFilters(prev => ({ ...prev, invitationState: '' }))}
                          >
                            ✕
                          </span>
                        </div>
                      );
                    }

                    if (key === 'updatedRecently' && filters.updatedRecently) {
                      const label =
                        filters.updatedRecently === '7d'
                          ? 'Last 7 days'
                          : filters.updatedRecently === '30d'
                            ? 'Last 30 days'
                            : 'Last 90 days';
                      pushWithAnd(
                        <div className="filter-pill" key="updatedRecently-pill">
                          Updated: {label}
                          <span
                            className="filter-pill-remove"
                            onClick={() => setFilters(prev => ({ ...prev, updatedRecently: '' }))}
                          >
                            ✕
                          </span>
                        </div>
                      );
                    }

                    if (key === 'multipleDepartments' && filters.multipleDepartments) {
                      pushWithAnd(
                        <div className="filter-pill" key="multipleDepartments-pill">
                          Multiple departments
                          <span
                            className="filter-pill-remove"
                            onClick={() => setFilters(prev => ({ ...prev, multipleDepartments: false }))}
                          >
                            ✕
                          </span>
                        </div>
                      );
                    }
                  }

                  return pills;
                })()}
              </div>
            </div>
          )}

          {isDisabledLocationFilterBlocked && (
            <DisabledLocationAccessBlockedBanner disabledLocations={blockedDisabledLocations} />
          )}

          {!isDisabledLocationFilterBlocked && (
            <>
              <div className="users-results-info">
                Showing {paginatedUsers.length} of {filteredUsers.length} users
                {canExportUsers && filteredUsers.length > 0 && (
                  <span className="users-export-hint">
                    {' '}— Export includes all {filteredUsers.length} filtered users
                  </span>
                )}
              </div>

              {canExportUsers && filteredUsers.length > 0 && (
                <div className="users-list-export-section">
                  <ExportButton
                    onExportPDF={handleExportUsersPdf}
                    onExportExcel={handleExportUsersExcel}
                    title="Export filtered users"
                    disabled={isExportingUsers}
                  />
                  <button
                    type="button"
                    className="users-export-config-btn"
                    onClick={() => setShowExportColumnsConfig(true)}
                    title="Configure export columns"
                    aria-label="Configure export columns"
                  >
                    ⚙️
                  </button>
                </div>
              )}
            </>
          )}

          <div className="users-table-container">
            <table className="users-table">
            <thead>
              <tr>
                <th
                  className="col-user-info"
                  onClick={() => handleSortChange('username')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Click to sort by username"
                >
                  User Info{getSortIndicator('username')}
                </th>
                <th
                  onClick={() => handleSortChange('full_name')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Click to sort by full name"
                >
                  Full Name{getSortIndicator('full_name')}
                </th>
                <th
                  className="col-contact"
                  onClick={() => handleSortChange('email')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Click to sort by email"
                >
                  Contact Info{getSortIndicator('email')}
                </th>
                <th
                  className="center col-role"
                  onClick={() => handleSortChange('role')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Click to sort by role"
                >
                  Role{getSortIndicator('role')}
                </th>
                <th
                  className="col-department"
                  onClick={() => handleSortChange('department')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Click to sort by department"
                >
                  Department{getSortIndicator('department')}
                </th>
                <th
                  className="center"
                  onClick={() => handleSortChange('location')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Click to sort by location"
                >
                  Location{getSortIndicator('location')}
                </th>
                <th
                  className="center"
                  onClick={() => handleSortChange('status')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Click to sort by status"
                >
                  Status{getSortIndicator('status')}
                </th>
                <th
                  className="center col-dates"
                  onClick={() => handleSortChange('updated_at')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Updated date (Created shown below) — click to sort"
                >
                  Updated{getSortIndicator('updated_at')}
                </th>
                <th className="center col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.map((user) => {
                const userDepts = getUserDepartmentsForDisplay(user);
                return (
                <tr key={user.id} className={!user.is_active ? 'inactive-user' : ''}>
                  <td className="col-user-info">
                    <div className="user-info-stacked">
                      <div className="username-stack">{user.username}</div>
                      <EmployeeIDBadge employeeId={user.employee_id} size="small" />
                    </div>
                  </td>
                  <td>{user.full_name}</td>
                  <td className="col-contact">
                    <div className="contact-info-stacked">
                      <div className="email-stack">
                        <span className="contact-icon">✉️</span>
                        <span className="contact-text" title={user.email || undefined}>{user.email}</span>
                      </div>
                      <div className="contact-number-stack">
                        <span className="contact-icon">📞</span>
                        <span className="contact-text">
                          {user.contact_number ? formatPhoneNumber(user.contact_number) : '-'}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="col-role">
                    <RoleBadge role={user.role} size="small" />
                  </td>
                  <td className="col-department">
                    {userDepts.length === 0 ? (
                      <span className="department-cell">
                        <span className="department-text">—</span>
                      </span>
                    ) : userDepts.length === 1 ? (
                      <span className="department-cell" title={userDepts[0].name || undefined}>
                        <span className="department-icon-small" aria-hidden="true">
                          {userDepts[0].icon || '🏢'}
                        </span>
                        <span className="department-text">{userDepts[0].name}</span>
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="department-multi-trigger"
                        aria-haspopup="dialog"
                        aria-label={`View ${userDepts.length} departments for ${user.full_name || user.username}`}
                        onClick={() => setDepartmentsModalContext({
                          userName: user.full_name || user.username,
                          departments: userDepts,
                        })}
                      >
                        <span className="department-multi-trigger__count" aria-hidden="true">
                          {userDepts.length}
                        </span>
                        <span className="department-multi-trigger__label">depts</span>
                        <span className="department-multi-trigger__arrow" aria-hidden="true">→</span>
                      </button>
                    )}
                  </td>
                  <td className="center">
                    {(() => {
                      // Get location names for this user from location_ids
                      const userLocationNames = (user.location_ids || [])
                        .map(locId => locations.find(loc => loc.id === locId)?.name)
                        .filter(Boolean); // Remove undefined values

                      return (
                        <span
                          className="users-table-location"
                          title={userLocationNames.join(', ') || 'No locations'}
                        >
                          📍 {userLocationNames.length > 0
                            ? userLocationNames.join(', ')
                            : '-'}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="center">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'center' }}>
                      <span className={`user-status-badge ${user.is_active ? 'active' : 'inactive'}`} title={user.is_active ? 'Active' : 'Inactive'}>
                        {user.is_active ? '✅' : '⏸️'}
                      </span>
                      {/* Phase 1 & 2: Show link status (verification email or reactivation link) */}
                      {!user.is_active && user.pending_link && !user.pending_link.is_expired ? (
                        !user.email_verified ? (
                          // Phase 1: Verification email status
                          <span
                            className="verification-link-badge"
                            title={`Verification email sent on ${new Date(user.pending_link.created_at).toLocaleDateString()} at ${new Date(user.pending_link.created_at).toLocaleTimeString()}`}
                            style={{
                              fontSize: '0.7rem',
                              padding: '0.15rem 0.35rem',
                              backgroundColor: '#e3f2fd',
                              color: '#1565c0',
                              borderRadius: '3px',
                              whiteSpace: 'nowrap',
                              fontWeight: '500'
                            }}
                          >
                            📧 {getTimeSinceLinkSent(user.pending_link.created_at)}
                          </span>
                        ) : (
                          // Phase 2: Reactivation link status
                          <span
                            className="reactivation-link-badge"
                            title={`Reactivation link sent on ${new Date(user.pending_link.created_at).toLocaleDateString()} at ${new Date(user.pending_link.created_at).toLocaleTimeString()}`}
                            style={{
                              fontSize: '0.7rem',
                              padding: '0.15rem 0.35rem',
                              backgroundColor: '#fff3cd',
                              color: '#856404',
                              borderRadius: '3px',
                              whiteSpace: 'nowrap',
                              fontWeight: '500'
                            }}
                          >
                            🔗 {getTimeSinceLinkSent(user.pending_link.created_at)}
                          </span>
                        )
                      ) : null}
                    </div>
                  </td>
                  <td
                    className="stacked-info center col-dates"
                    title={[
                      user.updated_at ? `Updated: ${new Date(user.updated_at).toLocaleString()}` : null,
                      user.created_at ? `Created: ${new Date(user.created_at).toLocaleString()}` : null
                    ].filter(Boolean).join(' | ')}
                  >
                    {user.updated_at || user.created_at ? (
                      <>
                        <div className="date-primary">
                          {user.updated_at
                            ? formatDateTime(user.updated_at).date
                            : '—'}
                        </div>
                        {user.created_at && (
                          <div className="date-secondary">
                            Created {formatDateTime(user.created_at).date}
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="na-text">N/A</span>
                    )}
                  </td>
                  <td className="actions center">
                    <ActionDropdown
                      user={user}
                      currentUser={currentUser}
                      isLoading={{
                        sendVerification: isSendingLink === user.id,
                        sendReactivation: isSendingLink === user.id,
                        revoke: isRevokingLink === user.id,
                        any: isSendingLink === user.id || isRevokingLink === user.id
                      }}
                      onEdit={() => handleEditUser(user)}
                      onSendVerificationEmail={() => handleSendPendingLink(user.id, user.full_name, user)}
                      onResendVerificationEmail={() => handleSendPendingLink(user.id, user.full_name, user)}
                      onSendReactivationLink={() => handleSendPendingLink(user.id, user.full_name, user)}
                      onResendReactivationLink={() => handleSendPendingLink(user.id, user.full_name, user)}
                      onRevokeLink={() => handleRevokeLinkClick(user.id, user.full_name, user.email_verified)}
                      onDelete={() => handleDeleteClick(user.id, user.full_name, user.username)}
                    />
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>

          {users.length === 0 && (
            <div className="empty-state">
              <p>No users found.</p>
            </div>
          )}

          {filteredUsers.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              perPage={perPage}
              onPerPageChange={handlePerPageChange}
              totalItems={filteredUsers.length}
              itemLabel="users"
            />
          )}
          </div>
        </>
      )}

      {/* REACTDOM PORTAL - Advanced Filters Drawer */}
      {showAdvancedPanel && ReactDOM.createPortal(
        <div className="sidebar-drawer-overlay users-filters-drawer-overlay" onClick={closeAdvancedFilters}>
          <div className="sidebar-drawer users-filters-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-title">
              <span>🔧 Advanced Filters</span>
              <button
                className="drawer-close-btn"
                onClick={closeAdvancedFilters}
                title="Close filters"
              >
                ✕
              </button>
            </div>

            <div className="drawer-filter-content">
              <div className="users-drawer-layout">
                {/* 1) Location (conditional, first) */}
                {canShowLocationFilter() && (
                  <div className="filter-group users-drawer-full">
                    <div className="filter-group-title">📍 Location</div>
                    <div className="filter-group-content users-two-col-content">
                      <div className="filter-item-wrapper">
                        <LocationFilterMultiSelect
                          locations={locations}
                          value={drawerFilters.locations || []}
                          onChange={(selected) =>
                            setDrawerFilters((prev) => ({ ...prev, locations: selected }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* 2) Department */}
                <div className="filter-group users-drawer-full">
                  <div className="filter-group-title">🏥 Department</div>
                  <div className="filter-group-content users-two-col-content">
                    <div className="filter-item-wrapper">
                      <select
                        className="filter-select"
                        multiple
                        value={drawerFilters.departments || []}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, option => option.value);
                          setDrawerFilters(prev => ({ ...prev, departments: selected }));
                        }}
                        style={{ minHeight: '80px' }}
                      >
                        {departments.map(dept => (
                          <option key={dept.id} value={dept.id}>{dept.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* 3) Roles + Status Group (2 column) */}
                <div className="users-drawer-two-col">
                  <div className="filter-group">
                    <div className="filter-group-title">👤 Roles</div>
                    <div className="filter-group-content users-two-col-content">
                      <div className="filter-item-wrapper full-width">
                        <select
                          className="filter-select"
                          multiple
                          value={drawerFilters.roles || []}
                          onChange={(e) => {
                            const selected = Array.from(e.target.selectedOptions, option => option.value);
                            setDrawerFilters(prev => ({ ...prev, roles: selected }));
                          }}
                          style={{ minHeight: '120px' }}
                        >
                          {uniqueRoles.map(role => (
                            <option key={role} value={role}>{formatRoleForSearch(role)}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="filter-group">
                    <div className="filter-group-title">📊 Status Group</div>
                    <div className="filter-group-content users-two-col-content">
                      <div className="filter-item-wrapper full-width">
                        <select
                          className="filter-select"
                          value={drawerFilters.statusGroup || ''}
                          onChange={(e) => setDrawerFilters(prev => ({ ...prev, statusGroup: e.target.value }))}
                        >
                          <option value="">All</option>
                          <option value="active">Active</option>
                          <option value="pending">Pending Verification</option>
                          <option value="deactivated">Deactivated</option>
                          <option value="password_setup">Password Setup Pending</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 4) Created Date Range */}
                <div className="filter-group users-drawer-full users-created-date-range">
                  <div className="filter-group-title">🗓️ Created Date Range</div>
                  <div className="filter-group-content users-two-col-content">
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">From</div>
                      <input
                        type="date"
                        className="filter-select"
                        style={{ padding: '0.5rem 0.75rem' }}
                        value={drawerFilters.createdDateFrom || ''}
                        onChange={(e) => {
                          const nextFrom = e.target.value;
                          if (drawerFilters.createdDateTo && nextFrom && nextFrom > drawerFilters.createdDateTo) {
                            setDrawerFilters(prev => ({ ...prev, createdDateFrom: nextFrom, createdDateTo: nextFrom }));
                          } else {
                            setDrawerFilters(prev => ({ ...prev, createdDateFrom: nextFrom }));
                          }
                        }}
                      />
                    </div>
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">To</div>
                      <input
                        type="date"
                        className="filter-select"
                        style={{ padding: '0.5rem 0.75rem' }}
                        value={drawerFilters.createdDateTo || ''}
                        onChange={(e) => {
                          const nextTo = e.target.value;
                          if (drawerFilters.createdDateFrom && nextTo && nextTo < drawerFilters.createdDateFrom) {
                            setDrawerFilters(prev => ({ ...prev, createdDateTo: nextTo, createdDateFrom: nextTo }));
                          } else {
                            setDrawerFilters(prev => ({ ...prev, createdDateTo: nextTo }));
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* 5) Last Login Range */}
                <div className="filter-group users-drawer-full">
                  <div className="filter-group-title">🕒 Last Login</div>
                  <div className="filter-group-content users-two-col-content">
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">From</div>
                      <input
                        type="date"
                        className="filter-select"
                        style={{ padding: '0.5rem 0.75rem' }}
                        value={drawerFilters.lastLoginFrom || ''}
                        onChange={(e) => {
                          const nextFrom = e.target.value;
                          if (drawerFilters.lastLoginTo && nextFrom && nextFrom > drawerFilters.lastLoginTo) {
                            setDrawerFilters(prev => ({ ...prev, lastLoginFrom: nextFrom, lastLoginTo: nextFrom }));
                          } else {
                            setDrawerFilters(prev => ({ ...prev, lastLoginFrom: nextFrom }));
                          }
                        }}
                      />
                    </div>
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">To</div>
                      <input
                        type="date"
                        className="filter-select"
                        style={{ padding: '0.5rem 0.75rem' }}
                        value={drawerFilters.lastLoginTo || ''}
                        onChange={(e) => {
                          const nextTo = e.target.value;
                          if (drawerFilters.lastLoginFrom && nextTo && nextTo < drawerFilters.lastLoginFrom) {
                            setDrawerFilters(prev => ({ ...prev, lastLoginTo: nextTo, lastLoginFrom: nextTo }));
                          } else {
                            setDrawerFilters(prev => ({ ...prev, lastLoginTo: nextTo }));
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* 6) Invitation state + Updated recently (2 column) */}
                <div className="users-drawer-two-col">
                  <div className="filter-group">
                    <div className="filter-group-title">📨 Invitation State</div>
                    <div className="filter-group-content users-two-col-content">
                      <div className="filter-item-wrapper full-width">
                        <select
                          className="filter-select"
                          value={drawerFilters.invitationState || ''}
                          onChange={(e) => setDrawerFilters(prev => ({ ...prev, invitationState: e.target.value }))}
                        >
                          <option value="">All</option>
                          <option value="active">Active link</option>
                          <option value="expired">Expired link</option>
                          <option value="none">No link sent</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="filter-group">
                    <div className="filter-group-title">🛠️ Updated Recently</div>
                    <div className="filter-group-content users-two-col-content">
                      <div className="filter-item-wrapper full-width">
                        <select
                          className="filter-select"
                          value={drawerFilters.updatedRecently || ''}
                          onChange={(e) => setDrawerFilters(prev => ({ ...prev, updatedRecently: e.target.value }))}
                        >
                          <option value="">Any time</option>
                          <option value="7d">Last 7 days</option>
                          <option value="30d">Last 30 days</option>
                          <option value="90d">Last 90 days</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="drawer-buttons">
                <button
                  className="cancel-btn"
                  onClick={() => setShowAdvancedPanel(false)}
                  title="Close filters without applying changes"
                >
                  Close
                </button>
                <button
                  className="reset-btn"
                  onClick={() => setDrawerFilters(createInitialUserFilterState())}
                >
                  Reset
                </button>
                <button
                  className="apply-btn"
                  onClick={() => {
                    if (drawerFilters.createdDateFrom && drawerFilters.createdDateTo) {
                      if (drawerFilters.createdDateFrom > drawerFilters.createdDateTo) {
                        toast.error('Created From date cannot be greater than To date');
                        return;
                      }
                    }
                    if (drawerFilters.lastLoginFrom && drawerFilters.lastLoginTo) {
                      if (drawerFilters.lastLoginFrom > drawerFilters.lastLoginTo) {
                        toast.error('Last Login From date cannot be greater than To date');
                        return;
                      }
                    }
                    setFilters(drawerFilters);
                    setShowAdvancedPanel(false);
                    setCurrentPage(1);
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* User Form Modal */}
      {showForm && (
        <UserFormModal
          user={selectedUser}
          locations={locations}
          departments={departments}
          onSubmit={selectedUser ? handleUpdateUser : handleCreateUser}
          onCancel={resetForm}
          usernameError={usernameError}
          modalError={modalError}
          selectedLocations={selectedLocations}
          setSelectedLocations={setSelectedLocations}
          selectedDepartments={selectedDepartments}
          setSelectedDepartments={setSelectedDepartments}
          currentUser={currentUser}
        />
      )}

      {/* Delete Confirmation Modal - permanent delete only */}
      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        entityName={deleteConfirmation.userName}
        entityType="user"
        action="delete"
        customMessage={
          deleteConfirmation.isPendingVerification
            ? 'This will permanently delete this pending invitation (unverified user). Any pending verification email records may also be removed to allow deletion.'
            : 'This action cannot be undone. All associated data will be permanently removed.'
        }
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isLoading={isDeleting}
      />

      {/* Deactivate Confirmation Modal - active user with audit history */}
      <ConfirmationModal
        isOpen={deactivateConfirmation.isOpen}
        entityName={deactivateConfirmation.userName}
        entityType="user"
        action="deactivate"
        title="User Cannot Be Deleted"
        size="medium"
        variant="info"
        primaryMessage={(
          <>
            <strong>{deactivateConfirmation.userName}</strong> cannot be permanently deleted.
          </>
        )}
        customMessage="Activity records for this user must be retained for audit and compliance. You can deactivate the account instead, which will prevent sign-in while keeping those records intact."
        onConfirm={handleDeactivateConfirm}
        onCancel={handleDeactivateCancel}
        isLoading={isDeleting}
      >
        <div className="modal-info-callout modal-info-callout--warning">
          <p>
            Existing records will be preserved for audit and compliance.<br/>
            You can reactivate this user later if needed.
          </p>
        </div>
      </ConfirmationModal>

      {/* Cannot Delete Info Modal - already deactivated user with audit history */}
      <Modal
        isOpen={cannotDeleteInfo.isOpen}
        onClose={handleCannotDeleteInfoClose}
        title="User Cannot Be Deleted"
        size="medium"
        className="modal-info-layout"
        footer={(
          <button
            type="button"
            className="action-btn action-primary"
            onClick={handleCannotDeleteInfoClose}
          >
            Understood
          </button>
        )}
      >
        <div className="modal-info-layout-body">
          <div className="modal-info-layout-body__icon" aria-hidden="true">ℹ️</div>
          <p className="modal-info-layout-body__lead">
            <strong>{cannotDeleteInfo.userName}</strong> cannot be permanently deleted because their
            activity records must be retained for audit and compliance.
          </p>
          <p className="modal-info-layout-body__detail">
            This account is already deactivated, so no further change is needed.
          </p>
          <div className="modal-info-callout modal-info-callout--info">
            <p>
              Existing records remain preserved for audit and compliance.<br/>
              The user can still be reactivated later if needed.
            </p>
          </div>
        </div>
      </Modal>

      {/* Email/Reactivation Link Resend Confirmation Modal */}
      <ConfirmationModal
        isOpen={linkResendConfirmation.isOpen}
        entityName={linkResendConfirmation.userName}
        entityType={!linkResendConfirmation.emailVerified ? "verification email" : "reactivation link"}
        action="resend"
        actionVerb={!linkResendConfirmation.emailVerified ? "resend the verification email to" : "resend the reactivation link to"}
        customMessage={
          !linkResendConfirmation.emailVerified
            ? `A verification email was already sent ${getTimeSinceLinkSent(linkResendConfirmation.lastSentTime)}. Are you sure you want to send another one? The previous link will be invalidated.`
            : `A reactivation link was already sent ${getTimeSinceLinkSent(linkResendConfirmation.lastSentTime)}. Are you sure you want to send another one? The previous link will be invalidated.`
        }
        onConfirm={handleLinkResendConfirm}
        onCancel={handleLinkResendCancel}
        isLoading={isResendingLink}
      />

      {/* Revoke Link Confirmation Modal */}
      <ConfirmationModal
        isOpen={revokeConfirmation.isOpen}
        entityName={revokeConfirmation.userName}
        entityType={!revokeConfirmation.emailVerified ? "verification email link" : "reactivation link"}
        action="revoke"
        actionVerb={`revoke the ${!revokeConfirmation.emailVerified ? 'verification email link from' : 'reactivation link from'}`}
        customMessage={
          !revokeConfirmation.emailVerified
            ? 'This will revoke the pending verification email link. The user will need a new verification email to activate their account.'
            : 'This will revoke the pending reactivation link. The user will need a new reactivation link to access their account.'
        }
        onConfirm={handleRevokeConfirm}
        onCancel={handleRevokeCancel}
        isLoading={isRevokingLink !== null}
      />

      <ExportColumnConfigurationModal
        isOpen={showExportColumnsConfig}
        onClose={() => setShowExportColumnsConfig(false)}
        title="Configure User Export Columns"
        description="Select which columns to include when exporting the current filtered user list."
        columns={exportColumns}
        onColumnsChange={setExportColumns}
        pdfMaxColumns={PDF_EXPORT_MAX_COLUMNS}
      />

      <UserDepartmentsModal
        isOpen={Boolean(departmentsModalContext)}
        onClose={() => setDepartmentsModalContext(null)}
        userName={departmentsModalContext?.userName}
        departments={departmentsModalContext?.departments || []}
      />
    </div>
  );
}

export default Users;
